import { Fragment, ReactNode, useMemo } from "react";
import {
  Card,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import { JobPhase } from "generated/graphql";
import Title from "./Title";
import pluralMeasurementMaterial from "../../../constants/pluralMeasurementMaterial";
import MDButton from "components/MDButton";
import { Delete } from "@mui/icons-material";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { UpdateWorkOrderInputSchema } from "DDD/action-objects/WorkOrderItemsUpdate";
import EquipmentPieceOptions from "modules/equipmentPieces/EquipmentPieceOptions";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import CrewOptions from "modules/crews/CrewOptions";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";
import AddLabourCost from "modules/proposals/labourCosts/AddLabourCost";
import AddEquipmentCost from "modules/proposals/equipmentCosts/AddEquipmentCost";
import { sentenceCase } from "change-case";

function formatHours(seconds?: number | null) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "—";
  return (Number(seconds) / 3600).toFixed(1);
}

function getCrewHours(jobPhase: JobPhase, crewId?: string | null) {
  const crew = jobPhase?.proposalProduct?.crews?.find(
    (proposalCrew) => `${proposalCrew?.crewId ?? proposalCrew?.id}` === `${crewId}`
  );

  return formatHours(crew?.timeOnjobOverride ?? crew?.timeOnjob);
}

function getEquipmentRowDetails(jobPhase: JobPhase, equipmentId?: string | null) {
  const equipmentPiece = jobPhase?.proposalProduct?.equipmentPieces?.find(
    (proposalEquipmentPiece) =>
      `${proposalEquipmentPiece?.equipmentPieceId ?? proposalEquipmentPiece?.id}` ===
      `${equipmentId}`
  );

  return {
    quantity: equipmentPiece?.quantityOverride ?? equipmentPiece?.quantity ?? "—",
    hours: formatHours(equipmentPiece?.timeOnjobOverride ?? equipmentPiece?.timeOnjob),
  };
}

function parsePlantAddress(plantData: any) {
  return [
    plantData?.addressLine1,
    plantData?.addressLine2,
    plantData?.addressCity,
    plantData?.addressState,
    plantData?.addressZip,
  ]
    .filter(Boolean)
    .join(", ");
}

const denseTableSx = {
  tableLayout: "auto",
  width: "100%",
  "& .MuiTableCell-root": {
    py: 0.9,
    px: 1.25,
    borderColor: "rgba(0, 0, 0, 0.08)",
    verticalAlign: "middle",
  },
  "@media print": {
    tableLayout: "fixed",
    "& .MuiTableCell-root": {
      py: 0.45,
      px: 0.8,
      fontSize: "0.9em",
    },
  },
} as const;

const headerCellSx = {
  fontWeight: 700,
  backgroundColor: "#f5f5f5",
  color: "#344767",
} as const;

export default function JobDetails({
  jobPhase,
  sideContent,
}: {
  jobPhase: JobPhase;
  sideContent?: ReactNode;
}) {
  const { control, register, getValues } = useFormContext<UpdateWorkOrderInputSchema>();

  const {
    fields: equipmentPieceFields,
    remove: removeEquipment,
    append: equipmentPieceAppend,
  } = useFieldArray({
    control,
    name: "equipmentPieces",
  });
  const {
    fields: crewFields,
    remove: removeCrew,
    append: crewsAppend,
  } = useFieldArray({
    control,
    name: "crews",
  });

  const hasPlantProducts = jobPhase?.jobPhasePlants && jobPhase.jobPhasePlants.length > 0;
  const hasMaterialNoPlants =
    jobPhase?.jobPhaseMaterialNoPlants && jobPhase.jobPhaseMaterialNoPlants.length > 0;
  const hasProposalPlantProduct = Boolean(jobPhase?.proposalProduct?.plantProduct);

  const statementOfWorkHtml = useMemo(() => {
    const html =
      jobPhase?.proposalProduct?.statementOfWork ||
      jobPhase?.proposalProduct?.material?.statementOfWork ||
      "";

    return typeof html === "string" ? html.trim() : "";
  }, [jobPhase]);

  const plantRows = useMemo(() => {
    if (jobPhase.worksheet)
      return (jobPhase.jobPhasePlants ?? []).map((plant) => ({
        key: plant.id,
        plantName: plant.name,
        address: parsePlantAddress(plant.plant),
        material: plant.type,
        tons: `${Number(
          plant.worksheetMaterial?.quantity ?? plant.materialNeeded ?? 0
        ).toLocaleString("en-US", { maximumFractionDigits: 6 })} ${
          plant.worksheetMaterial?.unit ?? ""
        }`.trim(),
        rate: plant.worksheetMaterial
          ? Number(plant.worksheetMaterial.rate).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 8,
            })
          : "—",
        truckName: "—",
        truckCount: "—",
      }));
    if (!hasPlantProducts && !hasProposalPlantProduct) return [];

    const sourcePlants = hasPlantProducts
      ? jobPhase.jobPhasePlants ?? []
      : [
          {
            id: `proposal-plant-${
              jobPhase?.proposalProduct?.plantProduct?.plantProductId ?? "fallback"
            }`,
            plant: jobPhase?.proposalProduct?.plantProduct?.plant,
            plantProduct: {
              id: jobPhase?.proposalProduct?.plantProduct?.plantProductId,
              type: jobPhase?.proposalProduct?.plantProduct?.type,
            },
            name: jobPhase?.proposalProduct?.plantProduct?.plant?.name,
            type: jobPhase?.proposalProduct?.plantProduct?.type,
            materialNeeded: jobPhase?.proposalProduct?.material?.materialNeeded,
          },
        ];

    return (
      sourcePlants.flatMap((plant) => {
        const matchingTrucks =
          jobPhase.workOrderTransferTrucks?.filter((truck) => {
            const sameProposalPlantProduct =
              truck?.proposalPlantProductId && truck.proposalPlantProductId === plant?.id;

            const samePlantProduct =
              truck?.proposalPlantProduct?.plantProductId &&
              plant?.plantProduct?.id &&
              truck.proposalPlantProduct.plantProductId === plant.plantProduct.id;

            const samePlant =
              (truck?.proposalPlantProduct?.plant?.id ||
                truck?.proposalPlantProduct?.plant?.plantId) &&
              plant?.plant?.id &&
              (truck.proposalPlantProduct.plant.id === plant.plant.id ||
                truck.proposalPlantProduct.plant.plantId === plant.plant.id);

            return sameProposalPlantProduct || (samePlantProduct && samePlant);
          }) ?? [];

        if (!matchingTrucks.length) {
          return [
            {
              key: `${plant.id}-no-truck`,
              plantName: plant.plant?.name || plant.name || "Plant",
              address: parsePlantAddress(plant.plant),
              material: sentenceCase(plant.plantProduct?.type || plant.type || "Material"),
              tons: `${plant.materialNeeded ?? "—"} ${
                pluralMeasurementMaterial[jobPhase?.proposalProduct?.measurementMaterial] ?? ""
              }`.trim(),
              truckName: "—",
              truckCount: "—",
            },
          ];
        }

        return matchingTrucks.map((truck) => {
          const proposalTruck = jobPhase.proposalProduct?.transferTrucks?.find(
            (proposalTransferTruck) =>
              proposalTransferTruck?.proposalPlantProductId === truck?.proposalPlantProductId &&
              (`${proposalTransferTruck?.transferTruckId}` === `${truck?.transferTruckId}` ||
                proposalTransferTruck?.name === truck?.name)
          );

          return {
            key: `${plant.id}-${truck?.id}`,
            plantName: plant.plant?.name || plant.name || "Plant",
            address: parsePlantAddress(plant.plant),
            material: sentenceCase(plant.plantProduct?.type || plant.type || "Material"),
            tons: `${plant.materialNeeded ?? "—"} ${
              pluralMeasurementMaterial[jobPhase?.proposalProduct?.measurementMaterial] ?? ""
            }`.trim(),
            truckName: proposalTruck?.name || truck?.name || "—",
            truckCount:
              proposalTruck?.quantity ?? proposalTruck?.trucksNeeded ?? truck?.quantity ?? "—",
          };
        });
      }) ?? []
    );
  }, [hasPlantProducts, hasProposalPlantProduct, jobPhase]);

  return (
    <MDBox mb={2} className="no-break">
      <Card>
        <Title text="Job Phase Details" />
        <MDBox
          p={3}
          sx={{
            "@media print": {
              padding: 1,
            },
          }}
        >
          {statementOfWorkHtml ? (
            <MDBox mb={3} sx={{ "@media print": { marginBottom: 1 } }}>
              <TableContainer component={Paper}>
                <Table aria-label="statement of work" sx={denseTableSx}>
                  <TableBody>
                    <TableRow>
                      <TableCell variant="head" sx={headerCellSx}>
                        Job Phase Statement of Work
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <MDBox
                          sx={{
                            "& p": { margin: "0 0 8px 0" },
                            "& p:last-of-type": { marginBottom: 0 },
                            "& ul, & ol": { margin: "0 0 8px 18px", padding: 0 },
                            "& li": { marginBottom: "4px" },
                          }}
                          dangerouslySetInnerHTML={{ __html: statementOfWorkHtml }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          ) : null}

          <MDBox mb={3} sx={{ "@media print": { marginBottom: 1 } }}>
            <TableContainer component={Paper}>
              <Table aria-label="materials" sx={denseTableSx}>
                <TableBody>
                  {plantRows.length > 0 ? (
                    <>
                      <TableRow>
                        <TableCell variant="head" sx={headerCellSx}>
                          Plant
                        </TableCell>
                        <TableCell variant="head" sx={headerCellSx}>
                          Address
                        </TableCell>
                        <TableCell variant="head" sx={headerCellSx}>
                          Material
                        </TableCell>
                        <TableCell variant="head" align="right" sx={headerCellSx}>
                          {jobPhase.worksheet ? "Quantity" : "Tons"}
                        </TableCell>
                        {jobPhase.worksheet && (
                          <TableCell variant="head" align="right" sx={headerCellSx}>
                            Rate
                          </TableCell>
                        )}
                        <TableCell variant="head" align="right" sx={headerCellSx}>
                          Truck
                        </TableCell>
                        <TableCell variant="head" align="right" sx={headerCellSx}>
                          # of Trucks
                        </TableCell>
                      </TableRow>
                      {plantRows.map((row) => (
                        <TableRow key={row.key}>
                          <TableCell>{row.plantName}</TableCell>
                          <TableCell>{row.address || "—"}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {row.material}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{row.tons}</TableCell>
                          {jobPhase.worksheet && (
                            <TableCell align="right">{"rate" in row ? row.rate : "—"}</TableCell>
                          )}
                          <TableCell align="right">{row.truckName}</TableCell>
                          <TableCell align="right">{row.truckCount}</TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : hasMaterialNoPlants ? (
                    <>
                      <TableRow>
                        <TableCell variant="head" sx={headerCellSx}>
                          Material
                        </TableCell>
                        <TableCell variant="head" align="right" width="72px" sx={headerCellSx}>
                          Quantity
                        </TableCell>
                      </TableRow>
                      {jobPhase.jobPhaseMaterialNoPlants.map((material) => (
                        <TableRow key={material.id}>
                          <TableCell>{material.name}</TableCell>
                          <TableCell align="right">{material.quantity}</TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell variant="head" sx={headerCellSx}>
                        Materials
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>

          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <MDBox mb={3} sx={{ "@media print": { marginBottom: 1 } }}>
                <TableContainer component={Paper} sx={{ overflowX: "visible" }}>
                  <Table aria-label="labor" sx={denseTableSx}>
                    <TableBody>
                      <TableRow>
                        <TableCell variant="head" sx={headerCellSx}>
                          Labor
                        </TableCell>
                        <TableCell variant="head" align="right" width="72px" sx={headerCellSx}>
                          Hours
                        </TableCell>
                        <TableCell
                          variant="head"
                          align="center"
                          width="40px"
                          className="no-print"
                          sx={headerCellSx}
                        >
                          <AddLabourCost
                            compactTrigger
                            submitMsg="Add Labor"
                            onAdd={(laborIds) =>
                              laborIds?.forEach((id) => {
                                crewsAppend({
                                  id,
                                });
                              })
                            }
                          />
                        </TableCell>
                      </TableRow>
                      {crewFields?.map((crew, index) => (
                        <Fragment key={crew.id}>
                          <TableRow>
                            <TableCell
                              sx={{
                                display: "none",
                                "@media print": {
                                  display: "table-cell",
                                },
                              }}
                            >
                              {getValues(`crews.${index}.name`)}
                            </TableCell>
                            <TableCell className="no-print">
                              <CrewOptions>
                                {({ crewOptions }) => (
                                  <CustomSelect
                                    control={control}
                                    name={`crews.${index}.id`}
                                    selectVariant="outlined"
                                    size="small"
                                    data={crewOptions}
                                    fullWidth
                                    {...register(`crews.${index}.id`)}
                                  />
                                )}
                              </CrewOptions>
                            </TableCell>
                            <TableCell align="right">
                              {getCrewHours(jobPhase, getValues(`crews.${index}.id`))}
                            </TableCell>
                            <TableCell align="right" className="no-print">
                              <MDButton
                                variant="text"
                                color="error"
                                type="button"
                                sx={{ minHeight: 0, p: 0 }}
                                onClick={() => removeCrew(index)}
                              >
                                <Delete />
                              </MDButton>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>

              <MDBox mb={3} sx={{ "@media print": { marginBottom: 1 } }}>
                <TableContainer component={Paper} sx={{ overflowX: "visible" }}>
                  <Table aria-label="equipment" sx={denseTableSx}>
                    <TableBody>
                      <TableRow>
                        <TableCell variant="head" sx={headerCellSx}>
                          Equipment
                        </TableCell>
                        <TableCell variant="head" align="right" width="72px" sx={headerCellSx}>
                          Qty
                        </TableCell>
                        <TableCell variant="head" align="right" width="72px" sx={headerCellSx}>
                          Hours
                        </TableCell>
                        <TableCell
                          variant="head"
                          align="center"
                          width="40px"
                          className="no-print"
                          sx={headerCellSx}
                        >
                          <AddEquipmentCost
                            compactTrigger
                            submitMsg="Add Equipment"
                            isJobCosting
                            onAdd={(equipmentIds) => {
                              equipmentIds?.forEach((ep) => {
                                equipmentPieceAppend({
                                  id:
                                    typeof ep === "string" || typeof ep === "number" ? ep : ep?.id,
                                  name: ep?.name,
                                });
                              });
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      {equipmentPieceFields?.map((equipment, index) => {
                        const equipmentRow = getEquipmentRowDetails(
                          jobPhase,
                          getValues(`equipmentPieces.${index}.id`)
                        );

                        return (
                          <Fragment key={equipment.id}>
                            <TableRow>
                              <TableCell
                                sx={{
                                  display: "none",
                                  "@media print": {
                                    display: "table-cell",
                                  },
                                }}
                              >
                                {getValues(`equipmentPieces.${index}.name`)}
                              </TableCell>
                              <TableCell className="no-print">
                                <EquipmentPieceOptions>
                                  {({ equipmentPieceOptions, loading, search, pagination }) => (
                                    <Controller
                                      name={`equipmentPieces.${index}.id` as const}
                                      control={control}
                                      render={({ field }) => (
                                        <ScrollAsyncAutocomplete
                                          {...field}
                                          value={{
                                            label:
                                              equipmentPieceOptions.find(
                                                (x) => x.value === field.value
                                              )?.label ??
                                              getValues(`equipmentPieces.${index}.name`) ??
                                              "",
                                            value: field.value,
                                          }}
                                          options={equipmentPieceOptions}
                                          loading={loading}
                                          label=""
                                          search={search}
                                          onLoadMore={pagination.loadMore}
                                          hasMore={pagination.hasNextPage}
                                          textFieldProps={{
                                            size: "small",
                                            sx: {
                                              "& .MuiOutlinedInput-input": {
                                                padding: "8.5px 12px",
                                              },
                                              "& .MuiOutlinedInput-root": {
                                                minHeight: "unset",
                                              },
                                              "& .MuiFormLabel-root": {
                                                display: "none",
                                              },
                                            },
                                          }}
                                        />
                                      )}
                                    />
                                  )}
                                </EquipmentPieceOptions>
                              </TableCell>
                              <TableCell align="right">{equipmentRow.quantity}</TableCell>
                              <TableCell align="right">{equipmentRow.hours}</TableCell>
                              <TableCell align="right" className="no-print">
                                <MDButton
                                  variant="text"
                                  color="error"
                                  sx={{ minHeight: 0, p: 0 }}
                                  onClick={() => removeEquipment(index)}
                                >
                                  <Delete />
                                </MDButton>
                              </TableCell>
                            </TableRow>
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>

              {jobPhase?.proposalProduct?.additionalCosts.length > 0 && (
                <MDBox>
                  <TableContainer component={Paper}>
                    <Table aria-label="additional costs" sx={denseTableSx}>
                      <TableBody>
                        <TableRow>
                          <TableCell variant="head" sx={headerCellSx}>
                            Additional Cost
                          </TableCell>
                          <TableCell variant="head" align="right" width="72px" sx={headerCellSx}>
                            Quantity
                          </TableCell>
                        </TableRow>
                        {jobPhase?.proposalProduct?.additionalCosts?.map((additionalCost) => (
                          <TableRow key={additionalCost?.id}>
                            <TableCell>{additionalCost?.name}</TableCell>
                            <TableCell align="right">
                              {additionalCost?.quantityOverride || additionalCost?.quantity}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
              )}
            </Grid>

            {sideContent ? (
              <Grid item xs={12} md={6}>
                {sideContent}
              </Grid>
            ) : null}
          </Grid>
        </MDBox>
      </Card>
    </MDBox>
  );
}
